import React, { useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'use-intl';
import {
	formatValue,
	getSelectOptions
} from '@/pages/MicrocreditRequests/MicrocreditQuestionnaire/utils';
import {
	DaysPerSimulationPeriod,
	Maybe,
	Request,
	TiieBanxicoValue
} from '@/graphql/__generated__/van-credits';
import QuestionnaireInput from '@/pages/MicrocreditRequests/MicrocreditQuestionnaire/QuestionnaireInput';
import { Typography } from '@/UIlibrary/Typography/Typography';
import QuestionnaireEditableInput from '@/pages/MicrocreditRequests/MicrocreditQuestionnaire/QuestionnaireEditableInput';
import Toggle from '@/UIlibrary/Toggle/Toggle';
import { useSimulationStore } from '@/stores/useVanCreditSimulationStore';
import { formatDate } from '@/utils/utils';

interface SimulationInputsProps {
	data: Request;
	tiieBanxico: Maybe<TiieBanxicoValue> | undefined;
	inputErrors?: Record<string, string>;
}

enum inputErrorMapping {
	amountRequested = 'amount_requested_for_simulation',
	paymentCapacity = 'payment_capacity',
	daysPerPeriod = 'days_per_period',
	firstPaymentDate = 'first_payment_date',
	sameDayPayment = 'same_day_payment',
	percentageOfCreditToSettle = 'percentage_of_credit_to_settle',
	gpsAmount = 'gps_amount',
	insuranceAmount = 'insurance_amount',
	basisPoints = 'basis_points'
}

const SimulationInputs: React.FC<SimulationInputsProps> = ({ data, inputErrors, tiieBanxico }) => {
	const t = useTranslations('VanCreditRequestSimulation');
	const { formData, updateField, initializeFromData, resetForm } = useSimulationStore();
	const [forceUpdate, setForceUpdate] = useState(0);
	const [maximumPeriods, setMaximumPeriods] = useState(60);
	const [ordinaryInterestRate, setOrdinaryInterestRate] = useState(0);

	const simulationPaymentCapacity = data?.simulation?.configuration?.paymentCapacity;
	const simulationAmountRequested = data?.simulation?.configuration?.amountRequested;
	const simulationInterestRate = data?.simulation?.configuration?.ordinaryInterestRate;
	const simulationFirstPaymentDate = data?.simulation?.configuration?.firstPaymentDate;
	const requestId = data?.requestId;

	useEffect(() => {
		if (requestId) {
			resetForm();
			initializeFromData(data);

			setTimeout(() => {
				setForceUpdate((prev) => prev + 1);
			}, 5);
		}
	}, [
		requestId,
		simulationPaymentCapacity,
		simulationAmountRequested,
		simulationInterestRate,
		simulationFirstPaymentDate,
		data,
		initializeFromData,
		resetForm
	]);

	const defaultFirstPaymentDate = useMemo(() => {
		if (!formData.daysPerPeriod) return '';

		try {
			switch (formData.daysPerPeriod) {
				case DaysPerSimulationPeriod.BIWEEKLY:
					return new Date(new Date().setDate(new Date().getDate() + 15))
						.toISOString()
						.split('T')[0];
				case DaysPerSimulationPeriod.WEEKLY:
					return new Date(new Date().setDate(new Date().getDate() + 7)).toISOString().split('T')[0];
				case DaysPerSimulationPeriod.MONTHLY:
					return new Date(new Date().setDate(new Date().getDate() + 30))
						.toISOString()
						.split('T')[0];
				default:
					return '';
			}
		} catch (error) {
			console.error('Error calculating default first payment date:', error);
			return '';
		}
	}, [formData.daysPerPeriod]);

	useEffect(() => {
		if (formData.daysPerPeriod) {
			const calculatedDate = defaultFirstPaymentDate;
			if (calculatedDate) {
				updateField('firstPaymentDate', calculatedDate);
			}
		}
	}, [formData.daysPerPeriod, defaultFirstPaymentDate, updateField]);

	const maximumPeriodsMap = {
		[DaysPerSimulationPeriod.WEEKLY]: 240,
		[DaysPerSimulationPeriod.BIWEEKLY]: 120,
		[DaysPerSimulationPeriod.MONTHLY]: 60
	} as const;

	useEffect(() => {
		if (formData.daysPerPeriod) {
			setMaximumPeriods(maximumPeriodsMap[formData.daysPerPeriod]);
		}
	}, [formData.daysPerPeriod]);

	useEffect(() => {
		if (formData.basisPoints && tiieBanxico?.value) {
			const tiieValue = parseFloat(tiieBanxico?.value || '0');
			const basisPoints = parseFloat(formData.basisPoints);
			const ordinaryInterestRate = tiieValue + basisPoints;
			setOrdinaryInterestRate(ordinaryInterestRate);
		}
	}, [formData.basisPoints, tiieBanxico?.value]);

	const PERCENTAGE_BOUNDS = {
		MIN: 0,
		MAX: 90,
		DECIMAL_PLACES: 2
	} as const;

	const PERCENTAGE_FIELDS = new Set(['basisPoints', 'percentageOfCreditToSettle']);

	const validateAndClampPercentage = (value: any, fieldKey: string): string => {
		const stringValue = value?.toString() || '';

		if (stringValue === '' || stringValue === '.') return stringValue;

		const numericValue = parseFloat(stringValue);

		if (isNaN(numericValue)) return value;

		if (numericValue > PERCENTAGE_BOUNDS.MAX) {
			const currentValue = formData[fieldKey as keyof typeof formData] as string;
			return currentValue || '1';
		}

		if (numericValue < 0) {
			return '0';
		}

		const factor = Math.pow(10, PERCENTAGE_BOUNDS.DECIMAL_PLACES);
		return (Math.round(numericValue * factor) / factor).toString();
	};

	const isPercentageField = (fieldKey: string): boolean => PERCENTAGE_FIELDS.has(fieldKey);

	const handleChange = (fieldKey: string, value: any): void => {
		const processedValue = isPercentageField(fieldKey)
			? validateAndClampPercentage(value, fieldKey)
			: value;

		updateField(fieldKey as keyof typeof formData, processedValue);
	};

	return (
		<div className="px-6 mt-4" key={forceUpdate}>
			<div className="flex flex-col gap-1 p-4 bg-white rounded-lg w-full">
				<div className={`flex flex-row items-center mb-4 justify-between`}>
					<div className="grow">
						<Typography weight="semibold">{t('CreditSimulation')}</Typography>
					</div>
					<div className="flex flex-row items-center gap-2">
						<Typography size="sm" weight="bold">
							{data.simulation?.configuration?.tiieValue && data.simulation?.configuration?.tiieDate
								? `${t('TiieLastSimulation')}: ${data.simulation?.configuration?.tiieValue} @ ${formatDate(data.simulation?.configuration?.tiieDate || '')}`
								: ''}
						</Typography>
					</div>
				</div>
				<div className="flex flex-row flex-wrap">
					<div className="w-1/2 px-3 py-1">
						<QuestionnaireInput
							title={'calculatedMonthlyNetIncome'}
							value={formatValue(data.calculatedNetIncome, t)}
							translationsSource="VanCreditRequestSimulation"
						/>
					</div>
					<div className="w-1/2 px-3 py-1">
						<QuestionnaireEditableInput
							isSelect
							options={getSelectOptions('daysPerPeriod', t)}
							onChange={(newValue) => handleChange('daysPerPeriod', newValue)}
							title={'DaysPerPeriod'}
							value={formData.daysPerPeriod}
							translationsSource="VanCreditRequestSimulation"
							error={inputErrors?.[inputErrorMapping.daysPerPeriod] || ''}
						/>
					</div>
					<div className="w-1/2 px-3 py-1">
						<QuestionnaireInput
							title={'AmountRequested'}
							value={`${formatValue(data.creditDetails?.amountRequested, t)}`}
							translationsSource="VanCreditRequestSimulation"
						/>
					</div>
					<div className="w-1/2 px-3 py-1 items-center">
						<QuestionnaireInput
							title={'MaximumPeriods'}
							value={maximumPeriods.toString()}
							translationsSource="VanCreditRequestSimulation"
						/>
					</div>
					<div className="w-1/2 px-3 py-1">
						<QuestionnaireEditableInput
							onChange={(newValue) => handleChange('paymentCapacity', newValue)}
							type="money"
							title={'PaymentCapacity'}
							value={formData.paymentCapacity || data.creditDetails?.amountRequested || ''}
							translationsSource="VanCreditRequestSimulation"
							error={inputErrors?.[inputErrorMapping.paymentCapacity] || ''}
						/>
					</div>
					<div className="w-1/2 px-3 py-1">
						<QuestionnaireEditableInput
							onChange={(newValue) => handleChange('firstPaymentDate', newValue)}
							type="date"
							title={'FirstPaymentDate'}
							value={formData.firstPaymentDate || defaultFirstPaymentDate}
							translationsSource="VanCreditRequestSimulation"
							error={inputErrors?.[inputErrorMapping.firstPaymentDate] || ''}
						/>
					</div>
					<div className="w-1/2 px-3 py-1">
						<QuestionnaireEditableInput
							onChange={(newValue) => handleChange('amountRequested', newValue)}
							type="money"
							title={'amountRequested'}
							value={formData.amountRequested}
							translationsSource="VanCreditRequestSimulation"
							error={inputErrors?.[inputErrorMapping.amountRequested] || ''}
						/>
					</div>
					<div className="w-1/2 px-3 py-1 items-center">
						<div className={`flex flex-row justify-between`}>
							<Typography size="sm" weight="medium">
								{t('SameDayPayment')}:
							</Typography>
							<Toggle
								size="medium"
								defaultChecked={formData.sameDayPayment}
								onChange={(newValue) => handleChange('sameDayPayment', newValue)}
							/>
						</div>
						<Typography size="sm" weight="medium" color="error">
							{inputErrors?.[inputErrorMapping.sameDayPayment] || ''}
						</Typography>
					</div>
					<div className="w-1/2 px-3 py-1">
						<QuestionnaireInput
							title={'TiieBanxico'}
							value={`${tiieBanxico?.value || ''} @ ${formatDate(tiieBanxico?.date || '') || ''}`}
							translationsSource="VanCreditRequestSimulation"
						/>
					</div>
					<div className="w-1/2 px-3 py-1">
						<QuestionnaireEditableInput
							onChange={(newValue) => handleChange('insuranceAmount', newValue)}
							type="money"
							title={'InsuranceAmount'}
							value={
								formData.insuranceAmount || data.simulation?.configuration?.insuranceAmount || ''
							}
							translationsSource="VanCreditRequestSimulation"
							error={inputErrors?.[inputErrorMapping.insuranceAmount] || ''}
						/>
					</div>
					<div className="w-1/2 px-3 py-1">
						<QuestionnaireEditableInput
							onChange={(newValue) => handleChange('basisPoints', newValue)}
							title={'BasisPoints'}
							value={formData.basisPoints}
							translationsSource="VanCreditRequestSimulation"
							type="number"
							error={inputErrors?.[inputErrorMapping.basisPoints] || ''}
						/>
					</div>
					<div className="w-1/2 px-3 py-1">
						<QuestionnaireEditableInput
							onChange={(newValue) => handleChange('gpsAmount', newValue)}
							type="money"
							title={'GPSAmount'}
							value={formData.gpsAmount || data.simulation?.configuration?.gpsAmount || ''}
							translationsSource="VanCreditRequestSimulation"
							error={inputErrors?.[inputErrorMapping.gpsAmount] || ''}
						/>
					</div>
					<div className="w-1/2 px-3 py-1">
						<QuestionnaireInput
							title={'OrdinaryInterestRate'}
							value={`${ordinaryInterestRate.toFixed(4)}%`}
							translationsSource="VanCreditRequestSimulation"
						/>
					</div>
					<div className="w-1/2 px-3 py-1">
						<QuestionnaireEditableInput
							onChange={(newValue) => handleChange('percentageOfCreditToSettle', newValue)}
							type="number"
							title={'PercentageOfCreditToSettle'}
							value={formData.percentageOfCreditToSettle}
							translationsSource="VanCreditRequestSimulation"
							error={inputErrors?.[inputErrorMapping.percentageOfCreditToSettle] || ''}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

export default SimulationInputs;
